/**
 * Input validation utilities for URL parameters and user inputs
 * Prevents injection attacks by validating format before use in API calls
 */

// UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Slug format: alphanumeric, hyphens, underscores (1-100 chars)
const SLUG_REGEX = /^[a-zA-Z0-9_-]{1,100}$/;

// Alphanumeric ID: letters, numbers, hyphens, underscores (1-100 chars)
const ALPHANUMERIC_ID_REGEX = /^[a-zA-Z0-9_-]{1,100}$/;

/**
 * Validates UUID format
 * @param {string} value - Value to validate
 * @returns {boolean}
 */
export const isValidUUID = (value) => {
  if (typeof value !== 'string') return false;
  return UUID_REGEX.test(value);
};

/**
 * Validates slug format (for placeId from URL path)
 * @param {string} value - Value to validate
 * @returns {boolean}
 */
export const isValidSlug = (value) => {
  if (typeof value !== 'string') return false;
  return SLUG_REGEX.test(value);
};

/**
 * Validates pointId - can be 'default', 'delivery', UUID, or alphanumeric
 * @param {string} value - Value to validate
 * @returns {boolean}
 */
export const isValidPointId = (value) => {
  if (typeof value !== 'string') return false;
  const reservedValues = ['default', 'delivery'];
  if (reservedValues.includes(value)) return true;
  return ALPHANUMERIC_ID_REGEX.test(value) || UUID_REGEX.test(value);
};

/**
 * Sanitizes a string by removing potentially dangerous characters
 * Use as fallback when validation fails but you need a safe value
 * @param {string} value - Value to sanitize
 * @param {number} maxLength - Maximum allowed length
 * @returns {string|null}
 */
export const sanitizeString = (value, maxLength = 100) => {
  if (typeof value !== 'string') return null;
  // Remove any character that's not alphanumeric, hyphen, or underscore
  const sanitized = value.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, maxLength);
  return sanitized || null;
};

/**
 * Validates and returns the value, or returns fallback if invalid
 * @param {string} value - Value to validate
 * @param {function} validator - Validation function
 * @param {string|null} fallback - Fallback value if validation fails
 * @returns {string|null}
 */
export const validateOrFallback = (value, validator, fallback = null) => {
  if (validator(value)) return value;
  return fallback;
};
